import type * as React from "react";
import {
  type ButtonProps as AriaButtonProps,
  Group,
  Text,
} from "react-aria-components";
import { Button, type Hierarchy as ButtonHierarchy } from "../Button.tsx";
import {
  type Hierarchy as LinkHierarchy,
  Link,
  type Props as LinkProps,
} from "../Link.tsx";

interface ActionBase {
  displayText: string;
  leadingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  trailingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
}

interface ActionLink extends Pick<LinkProps, "onPress" | "target">, ActionBase {
  kind: "link";
  link: string;
}

/**
 * Excess-property checking does not reach a predeclared action object, and a `kind`-less literal has
 * no discriminant to narrow the union by. Both would otherwise take `link` and have it ignored.
 */
interface NotLink {
  link?: never;
  target?: never;
}

interface ActionButtonBase
  extends Pick<AriaButtonProps, "onPress">, ActionBase, NotLink {
  loading?: boolean;
  disabled?: boolean;
}

interface ActionButton extends ActionButtonBase {
  kind: "button";
}

interface ActionIconOnly extends Pick<AriaButtonProps, "onPress">, NotLink {
  kind: "iconOnly";
  loading?: false;
  leadingIcon: React.FC<React.SVGProps<SVGSVGElement>>;
  ariaLabel?: string;
  disabled?: boolean;
}

interface ActionIconOnlyLoading
  extends Pick<AriaButtonProps, "onPress">, NotLink {
  kind: "iconOnly";
  loading: true;
  ariaLabel?: string;
  disabled?: boolean;
}

interface ActionDefault extends ActionButtonBase {
  kind?: undefined;
}

export type Action =
  | ActionLink
  | ActionButton
  | ActionIconOnly
  | ActionIconOnlyLoading
  | ActionDefault;

export type Actions = readonly Action[];

export interface Props {
  title: string;
  titleClassName: string;
  /** Rendered as a sibling immediately after the title, before the description (e.g. Alert's inline dismiss button). */
  titleAdornment?: React.ReactNode;
  description?: React.ReactNode;
  descriptionClassName?: string;
  actions?: Actions;
  actionsClassName?: string;
  /** Hierarchy used for `button`/`iconOnly` actions. The default (`kind` omitted) action always uses `tertiary`. */
  buttonHierarchy: ButtonHierarchy;
  /** Hierarchy used for `link` actions. */
  linkHierarchy: LinkHierarchy;
}

const defaultActions: Actions = [];

/**
 * Shared title/supporting-text/actions block used by `Alert` and `SystemNotification`. Renders a
 * fragment so callers control layout and skin; only structure and the actions switch live here.
 */
export const NotificationContent: React.FC<Props> = ({
  title,
  titleClassName,
  titleAdornment,
  description,
  descriptionClassName,
  actions = defaultActions,
  actionsClassName,
  buttonHierarchy,
  linkHierarchy,
}) => (
  <>
    <Text slot="title" className={titleClassName}>
      {title}
    </Text>
    {titleAdornment}
    {description != null && (
      <Text slot="description" className={descriptionClassName}>
        {description}
      </Text>
    )}
    {actions.length > 0 && (
      <Group className={actionsClassName}>
        {/* explicit type so TS errors on missed `action.kind` cases */}
        {actions.map((action, index): React.ReactElement => {
          switch (action.kind) {
            case "button":
            case undefined: {
              return (
                <Button
                  key={action.displayText}
                  size="xs"
                  hierarchy={
                    action.kind === "button" ? buttonHierarchy : "tertiary"
                  }
                  onPress={action.onPress}
                  leadingIcon={action.leadingIcon}
                  trailingIcon={action.trailingIcon}
                  variant="default"
                  loading={action.loading}
                  disabled={action.disabled}
                >
                  {action.displayText}
                </Button>
              );
            }
            case "iconOnly": {
              return action.loading === true ? (
                <Button
                  key={index}
                  size="xs"
                  hierarchy={buttonHierarchy}
                  loading={true}
                  variant="iconOnly"
                  ariaLabel={action.ariaLabel}
                  onPress={action.onPress}
                  disabled={action.disabled}
                />
              ) : (
                <Button
                  key={index}
                  size="xs"
                  hierarchy={buttonHierarchy}
                  leadingIcon={action.leadingIcon}
                  variant="iconOnly"
                  ariaLabel={action.ariaLabel}
                  onPress={action.onPress}
                  disabled={action.disabled}
                />
              );
            }
            case "link": {
              return (
                <Link
                  key={action.displayText}
                  size="xs"
                  href={action.link}
                  hierarchy={linkHierarchy}
                  onPress={action.onPress}
                  target={action.target}
                  leadingIcon={action.leadingIcon}
                  trailingIcon={action.trailingIcon}
                >
                  {action.displayText}
                </Link>
              );
            }
          }
        })}
      </Group>
    )}
  </>
);
